// ─────────────────────────────────────────────────────────────────
// AI content generation — API request schemas
//
// Canonical wire shapes for the "build my blog/email" endpoints
// (GET /api/ai/providers, POST /api/ai/drafts). Kept in @blogforge/shared
// so the editor can reuse the exact request/response types its api client
// sends, and the OpenAPI spec is generated from the same schemas.
// ─────────────────────────────────────────────────────────────────

import { z } from 'zod';
import { BlockSchema } from './blocks.js';
import { DesignTokensSchema } from './posts.js';

export const DraftKindSchema = z.enum(['post', 'email']);

/**
 * Optional working context for a draft request:
 *  - `document` — the current document (existing blocks/title/subject),
 *    so re-prompts ("make this more casual", "add a CTA") edit real content
 *    instead of inventing from scratch.
 *  - `selection` — a single canvas object the user wants to focus on
 *    ("rewrite this heading", "expand this paragraph into a section").
 *    Response shape is still a full Draft; the caller decides how to apply
 *    the returned blocks (e.g. replace the selection with them).
 */
export const DraftContextSchema = z
  .object({
    document: z
      .object({
        title: z.string().max(500).optional(),
        subject: z.string().max(500).optional(),
        previewText: z.string().optional(),
        blocks: z.array(BlockSchema).optional(),
      })
      .optional(),
    selection: z
      .object({
        blockId: z.string(),
        block: BlockSchema,
      })
      .optional(),
  })
  .optional();

export type DraftContext = z.infer<NonNullable<typeof DraftContextSchema>>;

export const AiDraftRequestSchema = z.object({
  kind: DraftKindSchema,
  /** Natural-language brief. */
  prompt: z.string().min(1).max(4000),
  /** Extra constraints appended to the skill-based system prompt. */
  extraInstructions: z.string().max(2000).optional(),
  /** Optional provider override (defaults to the site/env default). */
  providerId: z.string().optional(),
  /** Optional model override within the chosen provider (defaults to its default). */
  modelId: z.string().optional(),
  /** Current document / canvas selection used for re-prompt & object-focused edits. */
  context: DraftContextSchema,
});

export type AiDraftRequest = z.infer<typeof AiDraftRequestSchema>;

// ─────────────────────────────────────────────────────────────────
// Draft response shapes (POST /api/ai/drafts → draft)
// Same content contract the backend Ai interface returns, single-sourced
// here so the editor can parse/validate provider output client-side too.
// ─────────────────────────────────────────────────────────────────

export const AiPostDraftSchema = z.object({
  kind: z.literal('post'),
  title: z.string().min(1).max(500),
  excerpt: z.string().max(500).optional(),
  blocks: z.array(BlockSchema),
  designOverrides: DesignTokensSchema.optional(),
});

export type AiPostDraft = z.infer<typeof AiPostDraftSchema>;

export const AiEmailDraftSchema = z.object({
  kind: z.literal('email'),
  subject: z.string().min(1).max(500),
  previewText: z.string().optional(),
  blocks: z.array(BlockSchema),
  designOverrides: DesignTokensSchema.optional(),
});

export type AiEmailDraft = z.infer<typeof AiEmailDraftSchema>;

export const AiDraftSchema = z.discriminatedUnion('kind', [AiPostDraftSchema, AiEmailDraftSchema]);

export type AiDraft = z.infer<typeof AiDraftSchema>;

// ─────────────────────────────────────────────────────────────────
// API response shapes (GET /api/ai/providers, POST /api/ai/drafts)
// ─────────────────────────────────────────────────────────────────

export const AiModelInfoSchema = z.object({
  id: z.string(),
  label: z.string(),
  description: z.string().optional(),
  isDefault: z.boolean().optional(),
});

export type AiModelInfo = z.infer<typeof AiModelInfoSchema>;

export const AiProviderInfoSchema = z.object({
  providerId: z.string(),
  label: z.string(),
  mode: z.enum(['stub', 'live']),
  requiresKey: z.boolean(),
  apiKeyConfigured: z.boolean(),
  defaultModelId: z.string(),
  models: z.array(AiModelInfoSchema),
});

export type AiProviderInfo = z.infer<typeof AiProviderInfoSchema>;

export const AiProvidersResultSchema = z.object({
  providers: z.array(AiProviderInfoSchema),
  defaultProviderId: z.string(),
});

export type AiProvidersResult = z.infer<typeof AiProvidersResultSchema>;

export const AiDraftResultSchema = z.object({
  provider: z.string(),
  model: z.string(),
  draft: AiDraftSchema,
  /** Present when the draft consumed metered AI usage (micro-credits). */
  creditsChargedMicro: z.number().int().optional(),
  /** Workspace credit balance remaining after the charge. */
  creditsRemainingMicro: z.number().int().optional(),
});

export type AiDraftResult = z.infer<typeof AiDraftResultSchema>;
